import * as moment from 'moment';
import * as _ from 'lodash';
import { useEffect, useRef, useState } from 'react';
import { Glyphicon, Button, ButtonGroup, FormControl, OverlayTrigger, Popover, Label } from 'react-bootstrap';
import { IMatchingSurgeriesAndRedeyeResponse } from '@nkr/store/index';
import { IPosition, ICenter, IListItem, ICenterToCenterDistance, IDonor, IRecipient, ISoleSourceDonor } from '@nkr/models/index';
import { ChainStatus, ClusterStatus, MatchStatus, EmailStatusType } from '@nkr/enums/index';
import { DateProperties } from '../../models';
import { Utility } from '@nkr/services/utility';
import { FormControlWrapper } from '../../styles';
import { RowWrapper, SsdTableWrapper } from './styles';
import DateTimePicker from '@nkr/components/date-time-picker';
import SingleEpletMmRisk from '@nkr/views/swap-management/single-eplet-mm-risk';
import { Icon } from 'react-icons-kit';
import { checkCircleO } from 'react-icons-kit/fa/checkCircleO';
import { ban } from 'react-icons-kit/fa/ban';
import Trigger from 'rc-trigger';
import PlaceholderTable from './placeholder-table';
import AlleleMatch from '@nkr/components/allele-match';

import 'rc-dropdown/assets/index.css';
import PopoverTemplate from '@nkr/components/popover-template';

const getPopupAlign = (state) => {
    return {
        offset: [state.offsetX, state.offsetY],
        overflow: {
            adjustX: 1,
            adjustY: 1,
        },
    };
};

const getPopupContainer = (trigger) => {
    return trigger.parentNode;
};

const builtinPlacements = {
    left: {
        points: ['cr', 'cl'],
    },
    right: {
        points: ['cl', 'cr'],
    },
    top: {
        points: ['bc', 'tc'],
    },
    bottom: {
        points: ['tc', 'bc'],
    },
    topLeft: {
        points: ['bl', 'tl'],
    },
    topRight: {
        points: ['br', 'tr'],
    },
    bottomRight: {
        points: ['tr', 'br'],
    },
    bottomLeft: {
        points: ['tl', 'bl'],
    },
};

interface IProps {
    index: number,
    position: IPosition;
    chainStatusId: ChainStatus,
    clusterStatusId: ClusterStatus,
    swapChainStatusId: ChainStatus,
    swapClusterStatusId: ClusterStatus,
    pairStatusList: IListItem[],
    centerList: ICenter[],
    placeholder: number,
    distanceBetweenCentersList: ICenterToCenterDistance[],
    queryStatus: number,
    onShowMap: (url: string) => void,
    onEditLogistics: (position: number) => void,
    onResetChain: (recipientId: number) => void,
    onPairStatusChange: (positionNumber: number, pairStatusId: number) => void,
    onKidneyPickupChange: (positionNumber: number, kidneyPickup: string) => void,
    onKidneyDeliveryChange: (positionNumber: number, kidneyDelivery: string) => void,
    onMatchOfferDeadlineChange: (positionNumber: number, matchOfferDeadline: string) => void,
    onDonorClearanceDeadlineChange: (positionNumber: number, crossMatchDeadline: string) => void,
    onCreatePlaceholder: (centerId: number, centerAlias: string, donorId: number, hasAcceptableAnatomy: boolean) => void,
    onTriggerRexm: (donorId: number, recipientId: number) => void,
    onUpdateGps: (clusterId: number, positionNumber: number, toggle: boolean) => void,
    onCustomLogistics: (position: number) => void,
    onGetMatchingSurgeries: (dateValue: string, centerId: number, excludeId: number, isRecipient: boolean, positionNumber: number) => Promise<IMatchingSurgeriesAndRedeyeResponse>,
    onTogglePlaceholder: (show: boolean) => void,
    onShowOverlay: (donor: IDonor, recipient: IRecipient, soleSourceDonors: ISoleSourceDonor[], ssdHtml: string, target: any, intendedDonors: IDonor[]) => void,
    onHideOverlay: () => void
}

const PositionListRow = (props: IProps) => {
    const [showPlaceholder, setShowPlaceholder] = useState(false);
    const [placeholderLeft, setPlaceholderLeft] = useState('auto');
    const [selectFocused, setSelectFocused] = useState(false);

    const handlePairStatusChange = (e: React.FormEvent<FormControl & HTMLSelectElement>) => {
        props.onPairStatusChange(props.position.positionNumber, parseInt(e.currentTarget.value));
    };

    const handleChangeGps = () => {
        props.onUpdateGps(props.position.clusterId, props.position.positionNumber, props.position.gpsAutomation == null ? true : false);
    };

    const handleCreatePlaceholder = (centerId: number) => {
        setShowPlaceholder(false);

        let center = props.centerList.find(x => x.id == centerId);

        if (center != null) {
            let hasAcceptableAnatomy = position.excludedPlaceholderCenters.indexOf(centerId) < 0;

            props.onCreatePlaceholder(centerId, center.alias, props.position.donor.id, hasAcceptableAnatomy)
        }

        props.onTogglePlaceholder(false);
    }

    const handleCustomLogistics = () => {
        props.onCustomLogistics(props.position.positionNumber);
    };

    const handleEditLogistics = () => {
        props.onEditLogistics(props.position.positionNumber);
    };

    const handleShowMap = () => {
        let url = props.position.donor.logistics.mapLink;
        props.onShowMap(url);
    };

    const handleOpenGpsLink = () => {
        window.open(props.position.gpsAutomation.gpsLink);
    };

    const handleDateTimeValueChange = (property: string, moment: moment.Moment) => {
        switch (property) {
            case DateProperties.KIDNEY_PICKUP:
                if (position.kidneyPickup != moment.format('MM/DD/YY HH:mm')) {
                    props.onKidneyPickupChange(props.position.positionNumber, moment.format('MM/DD/YY HH:mm'));
                }
                break;

            case DateProperties.KIDNEY_ARRIVAL:
                if (position.kidneyArrival != moment.format('MM/DD/YY HH:mm')) {
                    props.onKidneyDeliveryChange(props.position.positionNumber, moment.format('MM/DD/YY HH:mm'));
                }
                break;

            case DateProperties.MATCH_OFFER_DEADLINE:
                props.onMatchOfferDeadlineChange(props.position.positionNumber, moment.format('MM/DD/YY HH:mm'));
                break;

            case DateProperties.DONOR_CLEARANCE_DEADLINE:
                props.onDonorClearanceDeadlineChange(props.position.positionNumber, moment.format('MM/DD/YY HH:mm'));
                break;
        }
    };

    const handlePlaceholderToggle = () => {
        let phButton = document.getElementById('ph-button-' + props.position.donor.id);
        let l = phButton.getBoundingClientRect().left;

        setPlaceholderLeft(l + 'px');
        setShowPlaceholder(!showPlaceholder);

        props.onTogglePlaceholder(!showPlaceholder);
    };

    const closePlaceholder = () => {
        setShowPlaceholder(false);
        props.onTogglePlaceholder(false);
    };

    const getCustomLogisticsIndicatorStyle = (): { color: string, title: string } => {
        switch (props.position.customLogisticsStatus) {
            case 1: return { color: '#cf1d1d', title: 'Existing draft plans' }; // red
            case 2: return { color: '#4f82e0', title: 'Plans submitted to NKR' }; // blue
            case 3: return { color: '#4f82e0', title: 'Existing NKR draft plans' };
            case 4: return { color: '#e0a44f', title: 'Plans submitted to Center' }; // amber
            case 5: return { color: '#64b06b', title: 'Plan selected by Center' }; // green
        }

        return { color: '#a2a5a6', title: 'No plans' };
    };

    const conflictsInOtherChains = (surgeryConflicts: string[]): string[] => {
        const chainId = String(props.position.chainId);

        return [...new Set(surgeryConflicts)].filter(x => x.split('/')[0] !== chainId);
    };

    const getMatchingSurgeriesOverlay = (isRecipient: boolean, matchingSurgeries: string[], surgeryDeclineDates: string[] = [], hasSurgicalDecline: boolean = false) => {
        const distinctSurgeries = conflictsInOtherChains(matchingSurgeries);
        let dateLength = distinctSurgeries.length;
        const reasons = props.position.matchOfferDeclineReasons;
        return (
            <Popover style={{ minWidth: 350, width: 'max-content', maxWidth: 900 }}>
                {distinctSurgeries.length > 0 &&
                    <>
                        <div style={{ marginBottom: 5, fontWeight: 550 }}>{isRecipient ? 'Other Recipient Surgeries' : 'Other Donor Surgeries'}</div>
                        <div>
                            {distinctSurgeries.join(', ')}
                        </div>
                    </>
                }
                {distinctSurgeries.length > 0 && surgeryDeclineDates.length > 0 &&
                    <hr className='solid' />
                }
                {isRecipient && surgeryDeclineDates.length > 0 && (
                    <>
                        <div
                            style={{
                                marginBottom: 5,
                                fontWeight: 550,
                                color: '#c9372e',
                                display: 'flex',
                                alignItems: 'center'
                            }}
                        >
                            {'Previously Declined Surgery Date'}
                            <Glyphicon glyph="ban-circle" style={{ marginLeft: 6 }} />
                        </div>
                        <div>
                            {[...new Set(surgeryDeclineDates)].join(', ')}
                        </div>
                    </>
                )}
                {isRecipient && hasSurgicalDecline && reasons && reasons.length > 0 && (
                    <><div style={{ marginTop: 15, marginBottom: '5px' }}>
                        <div style={{ marginTop: 15, color: '#c32232', fontSize: '16px' }}>
                            MO Declined Reasons
                        </div>
                    </div>
                        <ul style={{ paddingLeft: '1rem', marginBottom: 0, textAlign: 'left' }}>
                            {reasons.map((r, idx) => (
                                <li key={idx}>
                                    {r.reason == "Other" ? (
                                        <>
                                            <span>Other:</span>{' '}
                                            <span style={{ fontWeight: 'bold' }}>{r.otherText}</span>
                                        </>
                                    ) : (
                                        r.reason
                                    )}
                                </li>
                            ))}
                        </ul>
                        {reasons[0].nextAvailText && (
                            <div style={{ textAlign: 'left', whiteSpace: 'nowrap' }}>
                                <b>Alternative Surgery Dates:</b> {reasons[0].nextAvailText}
                            </div>
                        )}
                    </>
                )}
            </Popover>
        );
    };

    const renderDonorInformation = (index: number, donor: IDonor) => {
        return (
            <PopoverTemplate width='1000px' dismiss={false}>
                <SsdTableWrapper condensed striped bordered>
                    <thead>
                        <tr>
                            {index == 1 && (<>
                                <th>Min</th>
                                <th>Max</th>
                            </>)
                            }
                            <th>FAnat</th>
                            <th>CC</th>
                            <th>EBV</th>
                            <th>CMV</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            {index == 1 && (<>
                                <td>{donor.minWeeksUntilTransplant}</td>
                                <td>{donor.maxWeeksUntilTransplant}</td>
                            </>)
                            }
                            <td>{getWhichKidney(donor)}</td>
                            <td>{donor.creatinineClearance}</td>
                            <td>{donor.ebv}</td>
                            <td>{donor.cmv}</td>
                        </tr>
                    </tbody>
                </SsdTableWrapper>
            </PopoverTemplate>
        )
    }

    const getWhichKidney = (donor: IDonor) => {
        if (donor.whichKidney != '' && donor.whichKidney != 'DNK') {
            let results = `${donor.whichKidneyAbbreviated}${donor.numberOfArteries}`;

            if (donor.whichKidney == 'L-opt') {
                results += '(R)';
            }

            if (donor.whichKidney == 'R-opt') {
                results += '(L)';
            }

            return results;
        }

        return '--';
    };

    const renderChainStartPopover = (index: number, donor: IDonor) => {
        return (
            <OverlayTrigger placement='top' overlay={renderDonorInformation(index, donor)}>
                <a href={`/center/${donor.centerId}/donor/${donor.id}/profile`} target='_blank'>{donor.alias}</a>
            </OverlayTrigger>
        )
    }

    const position = props.position;
    const mapButtonStyle = Utility.getMapButtonStyle(position.donor.logistics);

    const hasUnosId = props.position.donor.unosId != '';
    const glyphUnosId = hasUnosId ? checkCircleO : ban;
    const glyphUnosIdColor = hasUnosId ? '#003' : 'red';

    const hasDonorSurgeon = props.position.donor.surgeonContactId > 0;
    const glyphDonorSurgeon = hasDonorSurgeon ? checkCircleO : ban;
    const glyphDonorSurgeonColor = hasDonorSurgeon ? '#003' : 'red';

    const hasRecipientSurgeon = props.position.recipient.surgeonContactId > 0;
    const glyphRecipientSurgeon = hasRecipientSurgeon ? checkCircleO : ban;
    const glyphRecipientSurgeonColor = hasRecipientSurgeon ? '#003' : 'red';

    const compatiblePairDateRange = position.compatiblePairStartDate && position.compatiblePairEndDate ? `${position.compatiblePairStartDate} - ${position.compatiblePairEndDate}` : '';
    const centerIdWhereDonationTakesPlace = position.donor.rdnCenterId > 0 ? position.donor.rdnCenterId : position.donor.center.id;
    const isStreamlined = position.matchHistory?.isStreamlinedCancellation ?? true;

    const emailStatusWidths: Record<EmailStatusType, string> = {
        [EmailStatusType.Cancelling]: "21ch",
        [EmailStatusType.Sending]: "21ch",
        [EmailStatusType.SendingTest]: "21ch",
        [EmailStatusType.Sent]: "15ch",
        [EmailStatusType.SentTest]: "15ch",
        [EmailStatusType.Cancelled]: "15ch",
        [EmailStatusType.NotSent]: "15ch",
        [EmailStatusType.Accepted]: "15ch",
        [EmailStatusType.Declined]: "15ch",
        [EmailStatusType.Commence]: "17ch",
    };

    const emailStatusLabels: Record<EmailStatusType, React.ReactNode> = {
        [EmailStatusType.Cancelling]: (
            <>
                <span className="spinner"></span>
                Cancelling Offer...
            </>
        ),
        [EmailStatusType.Sending]: (
            <>
                <span className="spinner"></span>
                Sending Offer...
            </>
        ),
        [EmailStatusType.SendingTest]: (
            <>
                <span className="spinner"></span>
                Sending Test Offer...
            </>
        ),
        [EmailStatusType.Sent]: "Offer Sent",
        [EmailStatusType.SentTest]: "Test Offer Sent",
        [EmailStatusType.Cancelled]: "Offer Cancelled",
        [EmailStatusType.NotSent]: "Offer Not Sent",
        [EmailStatusType.Accepted]: "Offer Accepted",
        [EmailStatusType.Declined]: "Offer Declined",
        [EmailStatusType.Commence]: "Commence Ready",
    };


    const emailStatusStyles: Record<EmailStatusType, string> = {
        [EmailStatusType.Cancelling]: "danger",
        [EmailStatusType.Sending]: "primary",
        [EmailStatusType.SendingTest]: "primary",
        [EmailStatusType.Sent]: "default",
        [EmailStatusType.SentTest]: "warning",
        [EmailStatusType.Cancelled]: "danger",
        [EmailStatusType.NotSent]: "default",
        [EmailStatusType.Accepted]: "success",
        [EmailStatusType.Declined]: "danger",
        [EmailStatusType.Commence]: "primary",
    };


    const emailStatusLabel =
        emailStatusLabels[props.position.emailStatus] ??
        ((!isStreamlined && position.matchHistory?.isCancelled)
            ? "Offer Cancelled"
            : !position.matchHistory
                ? "Not Sent"
                : position.matchHistory.isCancelled
                    ? "Not Sent"
                    : "Offer Sent");


    const emailStatusStyle =
        emailStatusStyles[props.position.emailStatus] ??
        ((props.position.emailStatus == EmailStatusType.Cancelling ||
            props.position.emailStatus == EmailStatusType.Cancelled ||
            (!isStreamlined && position.matchHistory?.isCancelled))
            ? "danger"
            : !position.matchHistory || position.matchHistory.isCancelled
                ? "default"
                : "success");



    const pairStatusList = props.pairStatusList.filter(x => {
        if (x.id == MatchStatus.Transplanted) { /* Do not filter out if status is not transplanted */
            if (((position.kidneyArrivalActual == '' || position.kidneyPickupActual == '') && centerIdWhereDonationTakesPlace != position.recipient.center.id) && props.chainStatusId != ChainStatus.Broken) {
                return false;
            } else {
                return true;
            }
        }

        return true;
    });

    const planIndicatorStyle = getCustomLogisticsIndicatorStyle();

    const surgeryDateConflictRedeyeColors = { color: 'white', backgroundColor: '#4949F5' };

    if (position.donor.id != -1 && position.recipient.id != -1) {
        let recipientClassName = '';

        if (position.recipient.isAdpChip && !position.recipient.hasAdpDonorDonated) {
            if (props.position.recipientDonationBeforeVhDonation) {
                recipientClassName = 'recipient-donation-before-vh-donation';
            } else {
                recipientClassName = 'recipient-voucher-donor-pending';
            }
        }

        let voucherDonorExpectedDonationDatePopover = null;

        if (recipientClassName != '' && position.recipient.adpDonor != null) {
            let {
                donationStartDate: vdDonationStartDate,
                donationEndDate: vdDonationEndDate,
                cluster: vdCluster,
                position: vdPositionNumber,
                statusId: vdStatusId,
                logistics: vdLogistics,
                alias,
                center,
                bloodType,
                age
            } = position.recipient.adpDonor;

            let donorExpectedDonationDate = vdLogistics.kidneyPickupDateTime;

            if (vdStatusId == MatchStatus.NeedsMatch) {
                recipientClassName = 'recipient-donation-vh-donor-needs-match';

                if (donorExpectedDonationDate == '') {
                    donorExpectedDonationDate = '- pending match -';
                }
            }

            let vdSwap = `${vdCluster.chainId}/${vdCluster.clusterNumber}/${vdPositionNumber}`;

            if (vdCluster.chainId == 0 && vdCluster.clusterNumber == 0 && vdPositionNumber == 0) {
                vdSwap = '- pending swap -';
            }

            let donationDateRange = `${vdDonationStartDate} - ${vdDonationEndDate}`;

            if (vdDonationStartDate == '' && vdDonationEndDate == '') {
                donationDateRange = '- not specified -';
            }

            voucherDonorExpectedDonationDatePopover =
                <Popover style={{ maxWidth: 500 }}>
                    <h4 style={{ marginTop: 0 }}>Voucher Donor - {alias}{center != null ? ` - ${center.alias}` : ''} </h4>
                    <table>
                        <tbody>
                            <tr>
                                <td style={{ paddingRight: 10 }}>Donation Date:</td>
                                <td>{donorExpectedDonationDate}</td>
                            </tr>
                            <tr>
                                <td style={{ paddingRight: 10 }}>Donation Range:</td>
                                <td>{donationDateRange}</td>
                            </tr>
                            <tr>
                                <td style={{ paddingRight: 10 }}>Donor ABO:</td>
                                <td>{bloodType}</td>
                            </tr>
                            <tr>
                                <td style={{ paddingRight: 10 }}>Donor Age:</td>
                                <td>{age}</td>
                            </tr>
                        </tbody>
                    </table>
                </Popover>;
        } else {
            voucherDonorExpectedDonationDatePopover =
                <Popover style={{ maxWidth: 500 }}>
                    <h4 style={{ marginTop: 0 }}>Voucher Donor</h4>
                    <div>The voucher donor is missing or unlinked...</div>
                </Popover>;
        }

        let intendedDonors = position.recipient.intendedDonors.filter(x => x.statusId == 5) //Find transplanted Intended Donor
        intendedDonors = intendedDonors.length > 0 ? intendedDonors : position.recipient.intendedDonors.filter(x => x.isExportable == true); //Use Exportable if no transplanted donor

        return (<>
            <RowWrapper>
                <td>{position.positionNumber}</td>
                <td>
                    <a href={'/center/' + position.donor.center.id + '/profile/edit/admin'} target='_blank'>{position.donor.center.alias}</a>
                    {position.donor.rdnCenterId > 0 && (
                        <div>(<span style={{ fontSize: 12, color: 'red' }}>{position.donor.rdnCenterAlias}</span>)</div>
                    )}
                </td>
                <td>{renderChainStartPopover(props.index + 1, position.donor)}</td>
                {props.clusterStatusId == ClusterStatus.Offers && (
                    <td>{position.recipient.center.cprScore < 0 ? "N/A" : (position.recipient.center.cprScore * 100).toFixed(1) + "%"}</td>
                )}
                <td className={recipientClassName}><a href={`center/${position.recipient.center.id}/profile/edit`} target='_blank'>{position.recipient.center.alias}</a></td>
                <td className={recipientClassName}>
                    <a href={`/center/${position.recipient.centerId}/recipient/${position.recipient.id}/profile/general`}
                        target='_blank'
                        onMouseEnter={(e) => {
                            props.onShowOverlay(
                                position.donor,
                                position.recipient,
                                position.donor.soleSourceDonors ?? [],
                                position.donor.ssdHtml ?? '',
                                e.currentTarget,
                                intendedDonors ?? []
                            );
                        }}
                        onMouseLeave={() => props.onHideOverlay()}
                    >
                        <span>{position.recipient.alias}</span>
                    </a>
                </td>
                <td>{position.donor.bloodType}</td>
                <td>{position.recipient.bloodType}</td>
                <td>{position.donor.age}</td>
                <td>{position.recipient.age}</td>
                <td>{position.pmp}</td>
                <td>{position.cPRA4}</td>
                <td><SingleEpletMmRisk data={position.singleEpletMmRisk} isAdmin={false} centerId={position.recipient.centerId} showExpectedWaitTimeBlue={true} /></td>
                <td><AlleleMatch bmtMm={position.bmtMm} displayAsInline={true} /></td>
                <td>{position.pts}</td>
                <td>{position.wt}</td>
                <td>
                    <FormControlWrapper
                        componentClass="select"
                        value={position.pairStatusId}
                        onChange={handlePairStatusChange}
                        onFocus={() => setSelectFocused(true)}
                        onBlur={() => setSelectFocused(false)}
                        style={{
                            color:
                                !selectFocused && position.pairStatusId == MatchStatus.NeedsMatch
                                    ? "#d85457"
                                    : !selectFocused && position.pairStatusId == MatchStatus.PendingCrossMatch
                                        ? "green"
                                        : undefined,
                            fontWeight: !selectFocused && position.pairStatusId == MatchStatus.NeedsMatch
                                ? '600'
                                : !selectFocused && position.pairStatusId == MatchStatus.PendingCrossMatch
                                    ? '600'
                                    : 'normal'

                        }}
                    >
                        {pairStatusList.map((item) => (
                            <option key={item.id} value={item.id}>
                                {item.abbreviation}
                            </option>
                        ))}
                    </FormControlWrapper>
                </td>

                {position.clusterStatusId == ClusterStatus.Logistics || position.clusterStatusId == ClusterStatus.Kickoff || position.clusterStatusId == ClusterStatus.InProcess ?
                    <>
                        <td>
                            <div style={{ paddingLeft: 10 }}>
                                <Glyphicon glyph={position.gpsAutomation != null ? 'check' : 'unchecked'} onClick={handleChangeGps} />
                            </div>
                        </td>

                        <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: 5 }}>
                                    <DateTimePicker
                                        property={DateProperties.KIDNEY_PICKUP}
                                        currentValue={position.kidneyPickup}
                                        borderColor={position.isPickupDateOutsideRange ? '#ff0000' : ''}
                                        borderWidth={position.isPickupDateOutsideRange ? 2 : 1}
                                        onValueChanged={handleDateTimeValueChange}
                                    />
                                </div>
                            </div>
                        </td>
                        <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: 5 }}>
                                    <DateTimePicker
                                        property={DateProperties.KIDNEY_ARRIVAL}
                                        currentValue={position.kidneyArrival}
                                        borderColor={position.isDeliveryDateOutsideRange ? '#ff0000' : ''}
                                        borderWidth={position.isDeliveryDateOutsideRange ? 2 : 1}
                                        titleText={compatiblePairDateRange}
                                        backgroundColor={position.kidneyArrival != '' && position.shouldBeRedeye ? '#EF9A9A' : ''}
                                        color={position.kidneyPickup != '' && position.shouldBeRedeye ? 'black' : ''}
                                        onValueChanged={handleDateTimeValueChange}
                                    />
                                </div>
                            </div>
                        </td>
                    </>
                    :
                    <>
                        {(props.swapChainStatusId == ChainStatus.Broken || props.swapChainStatusId == ChainStatus.Ended) && (
                            <td>
                                {position.transplantDate}
                            </td>
                        )}
                    </>
                }

                {position.clusterStatusId == ClusterStatus.Transplanted && (
                    <td>
                        <div style={{ paddingLeft: 10 }}>{position.transplantDate}</div>
                    </td>
                )}

                {position.clusterStatusId == ClusterStatus.Offers || position.clusterStatusId == ClusterStatus.CrossMatching ?
                    <>
                        <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: 5 }}>
                                    {conflictsInOtherChains(position.donorSurgeryConflicts).length > 0 ?
                                        <OverlayTrigger rootClose trigger={__ENVIRONMENT__ == 'dev' ? ['hover', 'focus'] : ['hover']} placement='top' overlay={getMatchingSurgeriesOverlay(false, position.donorSurgeryConflicts)}>
                                            <div>
                                                <DateTimePicker
                                                    property={DateProperties.KIDNEY_PICKUP}
                                                    currentValue={position.kidneyPickup}
                                                    borderColor={position.isPickupDateOutsideRange ? '#ff0000' : ''}
                                                    borderWidth={position.isPickupDateOutsideRange ? 2 : 1}
                                                    backgroundColor={'yellow'}
                                                    color={position.shouldBeRedeye ? 'black' : ''}
                                                    onValueChanged={handleDateTimeValueChange}
                                                />
                                            </div>
                                        </OverlayTrigger>
                                        :
                                        <DateTimePicker
                                            property={DateProperties.KIDNEY_PICKUP}
                                            currentValue={position.kidneyPickup}
                                            borderColor={position.isPickupDateOutsideRange ? '#ff0000' : ''}
                                            borderWidth={position.isPickupDateOutsideRange ? 2 : 1}
                                            onValueChanged={handleDateTimeValueChange}
                                        />
                                    }
                                </div>
                            </div>
                        </td>
                        <td>
                            <div style={{ display: 'flex', alignItems: 'center' }}>
                                <div style={{ marginRight: 5 }}>
                                    {conflictsInOtherChains(position.recipientSurgeryConflicts).length > 0 || position.recipientDeclinedDates.length > 0 ?
                                        <OverlayTrigger rootClose trigger={__ENVIRONMENT__ == 'dev' ? ['hover', 'focus'] : ['hover']} placement='top' overlay={getMatchingSurgeriesOverlay(true, position.recipientSurgeryConflicts, position.recipientDeclinedDates, position.hasSurgicalDecline)}>
                                            <div>
                                                <DateTimePicker
                                                    property={DateProperties.KIDNEY_ARRIVAL}
                                                    currentValue={position.kidneyArrival}
                                                    borderColor={position.isDeliveryDateOutsideRange ? '#ff0000' : ''}
                                                    borderWidth={position.isDeliveryDateOutsideRange ? 2 : 1}
                                                    titleText={compatiblePairDateRange}
                                                    backgroundColor={position.shouldBeRedeye ? surgeryDateConflictRedeyeColors.backgroundColor : 'yellow'}
                                                    color={position.shouldBeRedeye ? surgeryDateConflictRedeyeColors.color : ''}
                                                    onValueChanged={handleDateTimeValueChange}
                                                />
                                            </div>
                                        </OverlayTrigger>
                                        :
                                        <DateTimePicker
                                            property={DateProperties.KIDNEY_ARRIVAL}
                                            currentValue={position.kidneyArrival}
                                            borderColor={position.isDeliveryDateOutsideRange ? '#ff0000' : ''}
                                            borderWidth={position.isDeliveryDateOutsideRange ? 2 : 1}
                                            titleText={compatiblePairDateRange}
                                            backgroundColor={position.shouldBeRedeye ? '#EF9A9A' : ''}
                                            color={position.shouldBeRedeye ? 'black' : ''}
                                            onValueChanged={handleDateTimeValueChange}
                                        />
                                    }
                                </div>
                            </div>
                        </td>
                        <td>
                            <>{position.matchHistory ? (position.matchHistory.isCancelled == true) ? <Label bsStyle='danger' style={{ fontSize: '100%' }}>Pending Offer</Label> : position.matchHistory.deadlineDateTime : <Label bsStyle='danger' style={{ fontSize: '100%' }}>Pending Offer</Label>}</>
                        </td>

                        {(position.clusterStatusId == ClusterStatus.Offers || props.queryStatus == ClusterStatus.KflId) && (
                            <td>
                                <>
                                    {emailStatusLabel == "Offer Not Sent" ? (
                                        <Label
                                            style={{
                                                fontSize: "100%",
                                                display: "inline-block",
                                                width: emailStatusWidths[props.position.emailStatus],
                                                textAlign: "center",
                                                border: "1px solid #d35a52",
                                                color: "#d35a52",
                                                backgroundColor: "transparent",
                                                padding: ".2em .6em .3em", // ⬅️ add this
                                            }}
                                        >
                                            {emailStatusLabel}
                                        </Label>
                                    ) : emailStatusLabel == "Offer Sent" ? (
                                        <Label
                                            style={{
                                                fontSize: "100%",
                                                display: "inline-block",
                                                width: emailStatusWidths[props.position.emailStatus],
                                                textAlign: "center",
                                                color: "#198754",
                                                border: "1px solid #198754",
                                                backgroundColor: "transparent",
                                                padding: ".2em .6em .3em", // ⬅️ add this
                                            }}
                                        >
                                            {emailStatusLabel}
                                        </Label>
                                    ) : (
                                        <Label bsStyle={emailStatusStyle}
                                            style={{
                                                fontSize: "100%",
                                                display: "inline-block",
                                                width: emailStatusWidths[props.position.emailStatus],
                                                textAlign: "center",
                                                padding: ".2em .6em .3em", // ⬅️ add this
                                            }}>
                                            {emailStatusLabel}
                                        </Label>
                                    )}
                                </>
                            </td>

                        )}

                        {position.clusterStatusId == ClusterStatus.CrossMatching && (
                            <td style={{ textAlign: 'center' }}>
                                <ButtonGroup>
                                    {position.customLogisticsStatus > 0 && (
                                        <Button bsSize='xs' style={{ minWidth: 26, color: planIndicatorStyle.color }} title={planIndicatorStyle.title} onClick={handleCustomLogistics}><Glyphicon glyph='alert' /></Button>
                                    )}

                                    <Button bsSize='xs' style={{ minWidth: 26 }} onClick={handleEditLogistics}>L</Button>
                                    <Button bsSize='xs' style={{ minWidth: 26 }} bsStyle={mapButtonStyle} onClick={handleShowMap}>M</Button>

                                    {position.gpsAutomation != null && position.gpsAutomation.gpsLink != null && (
                                        <Button bsSize='xs' style={{ minWidth: 26 }} onClick={handleOpenGpsLink}>G{position.gpsAutomation.gpsNumber}</Button>
                                    )}
                                </ButtonGroup>
                            </td>
                        )}
                    </>
                    :
                    <>
                        <td style={{ textAlign: 'center' }}>
                            <ButtonGroup>
                                {position.customLogisticsStatus > 0 && (
                                    <Button bsSize='xs' style={{ minWidth: 26, color: planIndicatorStyle.color }} title={planIndicatorStyle.title} onClick={handleCustomLogistics}><Glyphicon glyph='alert' /></Button>
                                )}

                                <Button bsSize='xs' style={{ minWidth: 26 }} onClick={handleEditLogistics}>L</Button>
                                <Button bsSize='xs' style={{ minWidth: 26 }} bsStyle={mapButtonStyle} onClick={handleShowMap}>M</Button>

                                {position.gpsAutomation != null && position.gpsAutomation.gpsLink != null && (
                                    <Button bsSize='xs' style={{ minWidth: 26 }} onClick={handleOpenGpsLink}>G{position.gpsAutomation.gpsNumber}</Button>
                                )}
                            </ButtonGroup>
                        </td>
                    </>
                }

                {position.clusterStatusId == ClusterStatus.Kickoff && (
                    <td style={{ display: 'flex', justifyContent: 'center' }}>
                        <div style={{ width: 18, height: 18, marginRight: 2, color: glyphUnosIdColor }} title={hasUnosId ? 'Has UNOS ID' : 'No UNOS ID'}><Icon size={'100%'} icon={glyphUnosId} /></div>
                        <div style={{ width: 18, height: 18, marginRight: 2, color: glyphDonorSurgeonColor }} title={hasDonorSurgeon ? 'Has Donor Surgeon' : 'No Donor Surgeon'}><Icon size={'100%'} icon={glyphDonorSurgeon} /></div>
                        <div style={{ width: 18, height: 18, color: glyphRecipientSurgeonColor }} title={hasRecipientSurgeon ? 'Has Recipient Surgeon' : 'No Recipient Surgeon'}><Icon size={'100%'} icon={glyphRecipientSurgeon} /></div>
                    </td>
                )}

                <td></td>
            </RowWrapper>
        </>)
    }

    return (
        <RowWrapper>
            <td>{position.positionNumber}</td>
            <td>
                <a href={'/center/' + position.donor.center.id + '/profile/edit/admin'} target='_blank'>{position.donor.center.alias}</a>
                {position.donor.rdnCenterId > 0 && (
                    <div>(<span style={{ fontSize: 12, color: 'red' }}>{position.donor.rdnCenterAlias}</span>)</div>
                )}
            </td>
            <td>{renderChainStartPopover(props.index + 1, position.donor)}</td>
            <td colSpan={19}>
                <div className='wrapper' style={{ display: 'flex', alignItems: 'center', margin: 0, padding: '2px 0', border: '1px solid #CCC', backgroundColor: '#FFFFCC', borderRadius: 4 }}>
                    <div style={{ width: '12%' }}>
                        &nbsp; <strong>DB:</strong> {position.donor.bloodType}
                    </div>
                    <div style={{ width: '12%' }}>
                        &nbsp; <strong>DA:</strong> {position.donor.age}
                    </div>
                    {props.clusterStatusId != ClusterStatus.Stalled ?
                        <div style={{ width: '95%', display: 'flex', alignItems: 'center' }}>
                            <div style={{ fontWeight: 700, marginRight: 5 }}>Placeholder for:</div>
                            <div style={{ minWidth: 300 }}>
                                {showPlaceholder && (
                                    <div style={{
                                        position: 'fixed',
                                        top: 0,
                                        left: 0,
                                        right: 0,
                                        bottom: 0,
                                        backgroundColor: 'rgba(0,0,0,0.35)',
                                        zIndex: 1040
                                    }} />
                                )}
                                <Trigger
                                    // getPopupContainer={undefined && getPopupContainer}
                                    popupAlign={getPopupAlign({ offsetX: null, offsetY: null })}
                                    popupPlacement={'bottom'}
                                    destroyPopupOnHide={true}
                                    mask={false}
                                    maskClosable={false}
                                    stretch={null}
                                    builtinPlacements={builtinPlacements}
                                    popupStyle={{
                                        //border: '1px solid #ccc',
                                        borderRadius: 4,
                                        // background: 'black',
                                        // background: 'rgba(192, 193, 193, 0.5)',
                                        // opacity: '0.33',
                                        boxSizing: 'border-box',
                                        zIndex: 1050
                                        // width: '29%'
                                        // width: '100%',
                                        // height: '100%'
                                    }}
                                    popupVisible={showPlaceholder}
                                    popup={
                                        <div id={'ph-select-' + props.position.donor.id} style={{ backgroundColor: 'white', marginLeft: placeholderLeft, marginRight: 'auto', width: '34.8%', borderRadius: 4, boxShadow: '0px 0px 2px 2px rgba(18, 54, 102, 0.46)' }}>
                                            <PlaceholderTable
                                                position={props.position}
                                                centerList={props.centerList}
                                                distanceBetweenCentersList={props.distanceBetweenCentersList}
                                                onCreatePlaceholder={handleCreatePlaceholder}
                                                onCancel={handlePlaceholderToggle}
                                            />
                                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingTop: 3, paddingBottom: 4, paddingLeft: 5, paddingRight: 5, marginTop: 10 }}>
                                                <div>Default sort: Non-PC first, then PC; TCE descending within each group.</div>
                                                <div style={{ marginRight: 10 }}>
                                                    <Button bsSize='sm' bsStyle="primary" onClick={closePlaceholder}>Cancel</Button>
                                                </div>
                                            </div>
                                        </div>
                                    }
                                >
                                    <Button id={'ph-button-' + props.position.donor.id} bsSize='sm' block style={{ textAlign: 'left' }} onClick={handlePlaceholderToggle}>- Select -</Button>
                                </Trigger>
                            </div>
                        </div>
                        :
                        <Label>Placeholder selection not allowed in stalled tab</Label>
                    }
                </div>
            </td>
        </RowWrapper>
    );
}

export default PositionListRow;